import type {
    CalculationConfiguratorStore,
    CalculationProduct,
    InverterData,
    Products,
    RangePrice,
    ServicePackageData
} from "../../types/calculationProduct.ts";
import { ProductTypes } from "$lib/constants";
import type { BatteryData } from "$lib/products/batteries.ts";
import type { ModuleData } from "$lib/products/modules.ts";
import {
    type AdditionOptions,
    CharacteristicsConsumptionEnum,
    type Configurator,
    getEmptyConfiguratorData,
    type Insolation,
    useConfiguratorStore,
} from "$lib/store/configurator.store.ts";
import { fromStore } from "svelte/store";
import { type ConfiguratorSummaryStore, useConfiguratorSummaryStore, } from "$lib/store/configurator.summary.store.ts";
import { calculateFinancingTable } from "../../spc-platform/src/routes/api/financing/util.ts";
import { addOption } from "$lib/utils/additionalOption.calculate.helper.ts";
import { SummaryBuilder } from "$lib/utils/summary.calculate.helper.ts";
import { calculateQuantityForAppProduct } from "../../utils/cart.ts";
import { nonProduct } from "./nonProduct.ts";
import type { AppProduct } from "@shared/interfaces/product";
import { ProductType } from "@shared/interfaces/product";
import type { SelectedProducts } from "../../types/product";
import { createLogger } from "../logger.ts";

const logger = createLogger("CostCalculationService");

export type ProductCalculationSummary = ConfiguratorSummaryStore;

// ─────────────────────────────────────────────────────────────────────────────
// Price-mutation helpers (operate on CalculationProduct in place)
// ─────────────────────────────────────────────────────────────────────────────

const applyInverterRangePrice = (
    selectedInverter: CalculationProduct | undefined,
    pvSize: number,
): void => {
    if (!selectedInverter?.data) return;
    const inverterData = selectedInverter.data as InverterData;
    selectedInverter.price =
        (inverterData.range.length > 0
            ? inverterData.range.find(r => r.min <= pvSize && r.max >= pvSize)
            : null)?.price ?? selectedInverter.price;
};

const applyBatteryCapacityPrice = (
    selectedBattery: CalculationProduct | undefined,
    pvSize: number,
): void => {
    if (!selectedBattery?.data) return;
    const batteryData = selectedBattery.data as BatteryData;
    const capacityIndex = batteryData.capacities.findIndex(c => c >= pvSize);
    selectedBattery.price =
        capacityIndex !== -1
            ? batteryData.pricesWithInstallation[capacityIndex]
            : selectedBattery.price;
    batteryData.batteryPrice = selectedBattery.price;
    batteryData.batteryCapacity =
        capacityIndex !== -1
            ? batteryData.capacities[capacityIndex]
            : batteryData.batteryCapacity;
};

const applyServicePackageRangePrice = (
    selectedServicePackage: CalculationProduct | undefined,
    pvSize: number,
): void => {
    if (!selectedServicePackage?.data) return;
    const servicePackageData = selectedServicePackage.data as ServicePackageData;
    if (!servicePackageData.range) return;
    const rangePrice = servicePackageData.range.find(
        (range: RangePrice) => range.min <= pvSize && range.max >= pvSize,
    );
    selectedServicePackage.price = rangePrice ? rangePrice.price : selectedServicePackage.price;
};

/**
 * Delegates cost and financing calculations to the spc-platform store utilities.
 * All calculation-related spc-platform side-effects are contained here.
 */
export class CostCalculationService {

    private getSelectedProductStoreItems(
        selectedProducts: SelectedProducts,
        productsStore: Products,
    ): Products {
        return productsStore.filter(item =>
            Object.values(selectedProducts).some(p => p.id === item.id),
        );
    }

    getCostsCalculation(
        selectedProducts: SelectedProducts,
        productsStore: Products,
        moduleCount: number,
        kwPerYearAndModule: number,
        powerConsumption: number,
        costsPerKwh: number,
        address: AddressWithPrediction
    ): ProductCalculationSummary {
        logger.debug('calculating costs for selected products', { selectedProducts, moduleCount });

        const initialConfiguratorData = getEmptyConfiguratorData() as Configurator;

        initialConfiguratorData.roofSurfaces.modules.push({
            index: 0,
            roofOrientation: 235,
            roofSlope: 43,
            solarInstallation: 100,
            displayModule: moduleCount
        });

        initialConfiguratorData.insolation = {
            plz: address.postalAddress?.postalCode,
            source: 'google_solar', // @todo Solarplanner or Google? Or mixed?
            location: address.address,
            insolation: 939 // @todo - Get insolation from building insights or solarplanner api
        } as Insolation;

        initialConfiguratorData.characteristicsConsumption.characteristicsConsumption = CharacteristicsConsumptionEnum.DAY;

        initialConfiguratorData.amountOfModule = moduleCount;
        initialConfiguratorData.amountOfBattery = calculateQuantityForAppProduct(
            Object.values(selectedProducts).find(p => p.type === ProductType.BATTERY_STORAGE)!,
            moduleCount,
            kwPerYearAndModule,
        );
        initialConfiguratorData.electricityConsumption.currentPrice = costsPerKwh;
        initialConfiguratorData.electricityConsumption.electricityConsumption = powerConsumption;
        initialConfiguratorData.hasTax = true;

        const currentConfiguration: CalculationConfiguratorStore = initialConfiguratorData as Configurator;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useConfiguratorStore(currentConfiguration);

        const selectedItems = this.getSelectedProductStoreItems(selectedProducts, productsStore);
        currentConfiguration.additionOptions = new Array<AdditionOptions>();

        const selectedModule = selectedItems.find(i => i.type === ProductTypes.MODULE);
        const selectedBattery = selectedItems.find(i => i.type === ProductTypes.BATTERY);
        const selectedInverter = selectedItems.find(i => i.type === ProductTypes.INVESTER);
        const selectedServicePackage = selectedItems.find(
            i => i.type === ProductTypes.GENERAL &&
                (i.data as ServicePackageData)?.type === ProductType.SERVICE_PACKAGE,
        );
        const additionalItems = selectedItems.filter(
            i => i !== selectedModule &&
                i !== selectedBattery &&
                i !== selectedInverter &&
                i !== selectedServicePackage &&
                i.id !== nonProduct.id, // exclude "Keine Auswahl" placeholder
        );

        if (!selectedModule) {
            throw new Error("CostCalculationService: No module selected for cost calculation.");
        }

        currentConfiguration.pvSize =
            (moduleCount * ((selectedModule.data as AppProduct).powerClass! as number)) / 1000 || 0;

        applyInverterRangePrice(selectedInverter, currentConfiguration.pvSize);
        applyBatteryCapacityPrice(selectedBattery, currentConfiguration.pvSize);
        applyServicePackageRangePrice(selectedServicePackage, currentConfiguration.pvSize);

        currentConfiguration.moduleComponent = {
            moduleType: selectedModule.id,
            batterySystem: selectedBattery?.id || undefined,
        };

        addOption(
            selectedInverter!,
            calculateQuantityForAppProduct(
                Object.values(selectedProducts).find(p => selectedInverter!.id === p.id)!,
                moduleCount,
                kwPerYearAndModule,
            ),
        );
        if (selectedServicePackage) {
            addOption(
                selectedServicePackage,
                calculateQuantityForAppProduct(
                    Object.values(selectedProducts).find(p => selectedServicePackage.id === p.id)!,
                    moduleCount,
                    kwPerYearAndModule,
                ),
            );
        }
        additionalItems.forEach(item =>
            addOption(
                item,
                calculateQuantityForAppProduct(
                    Object.values(selectedProducts).find(p => p.id === item.id)!,
                    moduleCount,
                    kwPerYearAndModule,
                ),
            ),
        );

        logger.debug('Building summary with:', { productsStore, currentConfiguration })

        new SummaryBuilder(
            selectedModule.data as ModuleData,
            selectedBattery?.data as BatteryData || null,
            false,
            undefined,
            currentConfiguration,
            productsStore,
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        return fromStore(useConfiguratorSummaryStore()).current;
    }

    getFinancingTable(summaryStore: ProductCalculationSummary) {
        return calculateFinancingTable(summaryStore.economy?.totalCost || 0);
    }
}
